import React, { Fragment, useEffect } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input } from "antd";
import type { FormInstance } from "antd";

import {
  EARLY_PAYOUT_ACTION_LABELS,
  EARLY_PAYOUT_FORM_LABELS,
  EARLY_PAYOUT_PLACEHOLDERS,
  EARLY_PAYOUT_SOCCER_TOTAL_TIME_OVERLAP_VALIDATION_MESSAGE,
  EARLY_PAYOUT_SOCCER_TOTAL_TIME_RANGE_VALIDATION_MESSAGE,
  EARLY_PAYOUT_SOCCER_TOTAL_TIME_REQUIRED_MESSAGE,
  EARLY_PAYOUT_SOCCER_TOTAL_TIME_VALIDATION_MESSAGE,
  EARLY_PAYOUT_TEST_IDS,
  SOCCER_TOTAL_DEFAULT_CONDITIONS,
  SOCCER_TOTAL_DEFAULT_LINE_VALUE,
  SOCCER_TOTAL_LINE_STEP,
} from "./earlyPayoutConstants";
import type {
  CreateRulesetFormValues,
  EarlyPayoutSoccerTotalCondition,
} from "./earlyPayoutTypes";
import {
  canAdjustSoccerTotalLineValue,
  formatEarlyPayoutSoccerTotalTimeInput,
  formatSoccerTotalLineLabel,
  getNextAvailableSoccerTotalLineValue,
  getOverlappingSoccerTotalConditionIndexes,
  getSoccerTotalLineValues,
  isEarlyPayoutSoccerTotalTimeRangeValid,
  isValidEarlyPayoutSoccerTotalTime,
  roundSoccerTotalLineValue,
} from "./earlyPayoutUtils";
import styles from "./EarlyPayout.module.css";

type SoccerTotalConditionsProps = {
  form: FormInstance<CreateRulesetFormValues>;
};

const SoccerTotalConditions: React.FC<SoccerTotalConditionsProps> = ({ form }) => {
  const soccerTotalConditions =
    Form.useWatch("soccerTotalConditions", form) ?? [];

  useEffect(() => {
    const currentConditions = form.getFieldValue("soccerTotalConditions");
    if (!Array.isArray(currentConditions) || currentConditions.length === 0) {
      form.setFieldsValue({
        soccerTotalConditions: [...SOCCER_TOTAL_DEFAULT_CONDITIONS],
      });
    }
  }, [form]);

  const handleLineAdjust = (rowIndex: number, delta: number) => {
    const conditions: EarlyPayoutSoccerTotalCondition[] =
      form.getFieldValue("soccerTotalConditions") ?? [];
    const currentValue =
      Number(conditions[rowIndex]?.lineValue) || SOCCER_TOTAL_DEFAULT_LINE_VALUE;
    const existingValues = getSoccerTotalLineValues(conditions);

    if (!canAdjustSoccerTotalLineValue(currentValue, delta, existingValues, rowIndex)) {
      return;
    }

    const nextValue = roundSoccerTotalLineValue(currentValue + delta);
    const nextConditions = conditions.map((condition, index) =>
      index === rowIndex ? { ...condition, lineValue: nextValue } : condition,
    );

    form.setFieldsValue({ soccerTotalConditions: nextConditions });
  };

  const handleAddRow = (add: (defaultValue?: EarlyPayoutSoccerTotalCondition) => void) => {
    const conditions: EarlyPayoutSoccerTotalCondition[] =
      form.getFieldValue("soccerTotalConditions") ?? [];
    const lineValue = getNextAvailableSoccerTotalLineValue(
      getSoccerTotalLineValues(conditions),
    );

    add({
      lineValue,
      timeStart: "",
      timeEnd: "",
    });
  };

  return (
    <section
      className={styles["early-payout-conditions"]}
      data-testid={EARLY_PAYOUT_TEST_IDS.soccerTotalConditions}
    >
      <div className={styles["early-payout-conditions-heading"]}>
        {EARLY_PAYOUT_FORM_LABELS.conditions}
      </div>
      <div className={styles["early-payout-lines-card"]}>
        <div className={styles["early-payout-lines-grid"]}>
          <div className={styles["early-payout-lines-header"]}>
            {EARLY_PAYOUT_FORM_LABELS.lines}
            <span className={styles["early-payout-info-dot"]}>i</span>
          </div>
          <div className={styles["early-payout-lines-header"]}>
            {EARLY_PAYOUT_FORM_LABELS.timeStart}
            <span className={styles["early-payout-info-dot"]}>i</span>
          </div>
          <div className={styles["early-payout-lines-header"]}>
            {EARLY_PAYOUT_FORM_LABELS.timeEnd}
            <span className={styles["early-payout-info-dot"]}>i</span>
          </div>
          <div
            className={styles["early-payout-lines-header"]}
            aria-hidden="true"
          />
          <Form.List name="soccerTotalConditions">
            {(fields, { add, remove }) => {
              const canRemoveRow = fields.length > 1;

              return (
              <>
                {fields.map((field, index) => {
                  const lineValue =
                    Number(soccerTotalConditions[index]?.lineValue) ||
                    SOCCER_TOTAL_DEFAULT_LINE_VALUE;
                  const lineLabel = formatSoccerTotalLineLabel(lineValue);
                  const existingValues = getSoccerTotalLineValues(
                    soccerTotalConditions,
                  );
                  const canDecrease = canAdjustSoccerTotalLineValue(
                    lineValue,
                    -SOCCER_TOTAL_LINE_STEP,
                    existingValues,
                    index,
                  );
                  const canIncrease = canAdjustSoccerTotalLineValue(
                    lineValue,
                    SOCCER_TOTAL_LINE_STEP,
                    existingValues,
                    index,
                  );

                  return (
                    <Fragment key={field.key}>
                      <div className={styles["early-payout-line-control"]}>
                        <Button
                          type="text"
                          className={styles["early-payout-line-button"]}
                          aria-label={`Decrease ${lineLabel}`}
                          disabled={!canDecrease}
                          onClick={() => handleLineAdjust(index, -SOCCER_TOTAL_LINE_STEP)}
                        >
                          -
                        </Button>
                        <span className={styles["early-payout-line-value"]}>
                          {lineLabel}
                        </span>
                        <Form.Item hidden name={[field.name, "lineValue"]}>
                          <Input />
                        </Form.Item>
                        <Button
                          type="text"
                          className={styles["early-payout-line-button"]}
                          aria-label={`Increase ${lineLabel}`}
                          disabled={!canIncrease}
                          onClick={() => handleLineAdjust(index, SOCCER_TOTAL_LINE_STEP)}
                        >
                          +
                        </Button>
                      </div>
                      {(["timeStart", "timeEnd"] as const).map((fieldName) => {
                        const siblingFieldName =
                          fieldName === "timeStart" ? "timeEnd" : "timeStart";

                        return (
                        <div
                          key={fieldName}
                          className={styles["early-payout-time-field"]}
                        >
                          <Form.Item
                            noStyle
                            name={[field.name, fieldName]}
                            dependencies={[
                              ["soccerTotalConditions", field.name, siblingFieldName],
                              ["soccerTotalConditions"],
                            ]}
                            rules={[
                              {
                                required: true,
                                message:
                                  EARLY_PAYOUT_SOCCER_TOTAL_TIME_REQUIRED_MESSAGE,
                              },
                              {
                                validator: async (_, value) => {
                                  if (!isValidEarlyPayoutSoccerTotalTime(value)) {
                                    throw new Error(
                                      EARLY_PAYOUT_SOCCER_TOTAL_TIME_VALIDATION_MESSAGE,
                                    );
                                  }

                                  const siblingValue = form.getFieldValue([
                                    "soccerTotalConditions",
                                    field.name,
                                    siblingFieldName,
                                  ]);
                                  const timeStart =
                                    fieldName === "timeStart" ? value : siblingValue;
                                  const timeEnd =
                                    fieldName === "timeEnd" ? value : siblingValue;

                                  if (
                                    !isEarlyPayoutSoccerTotalTimeRangeValid(
                                      timeStart,
                                      timeEnd,
                                    )
                                  ) {
                                    throw new Error(
                                      EARLY_PAYOUT_SOCCER_TOTAL_TIME_RANGE_VALIDATION_MESSAGE,
                                    );
                                  }

                                  const conditions: EarlyPayoutSoccerTotalCondition[] =
                                    form.getFieldValue("soccerTotalConditions") ?? [];
                                  const overlappingIndexes =
                                    getOverlappingSoccerTotalConditionIndexes(
                                      conditions,
                                    );

                                  if (
                                    overlappingIndexes &&
                                    (overlappingIndexes[0] === field.name ||
                                      overlappingIndexes[1] === field.name)
                                  ) {
                                    throw new Error(
                                      EARLY_PAYOUT_SOCCER_TOTAL_TIME_OVERLAP_VALIDATION_MESSAGE,
                                    );
                                  }
                                },
                              },
                            ]}
                            getValueFromEvent={(event) =>
                              formatEarlyPayoutSoccerTotalTimeInput(
                                event.target.value,
                              )
                            }
                          >
                            <Input
                              className={styles["early-payout-time-input"]}
                              placeholder={EARLY_PAYOUT_PLACEHOLDERS.soccerTotalTime}
                              maxLength={5}
                              inputMode="numeric"
                              aria-label={`${lineLabel} ${
                                fieldName === "timeStart" ? "time start" : "time end"
                              }`}
                            />
                          </Form.Item>
                        </div>
                        );
                      })}
                      <div className={styles["early-payout-line-remove"]}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          className={styles["early-payout-remove-line-button"]}
                          aria-label={`${EARLY_PAYOUT_ACTION_LABELS.removeSoccerTotalLine} ${lineLabel}`}
                          disabled={!canRemoveRow}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    </Fragment>
                  );
                })}
                <div className={styles["early-payout-add-line-row"]}>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    className={styles["early-payout-add-line-button"]}
                    onClick={() => handleAddRow(add)}
                  >
                    {EARLY_PAYOUT_ACTION_LABELS.addSoccerTotalLine}
                  </Button>
                </div>
              </>
              );
            }}
          </Form.List>
        </div>
      </div>
    </section>
  );
};

export default SoccerTotalConditions;
